import Spinner from "../../../components/spinner";
import Errors from "../../errors";
import { useState } from "react";
import Pagination from "../../../components/elements/pagination";
import useGetCustomers from "../../../hooks/customers/useGetCustomers";
import useCreate from "../../../hooks/useCreate";
import { useMutation } from "@tanstack/react-query";
import { exportCustomers } from "../../../apis/customers";
import { toast } from "react-hot-toast";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../components/ui/table";
import PrimaryButton from "../../../components/elements/buttons/PrimaryButton";

function Customers() {
  const {
    data,
    handlePageClick,
    handleKeyDown,
    handleSetLimit,
    paramsObject,
    setGetListParams,
    initialData,
  } = useGetCustomers();

  const [searchInputs, setSearchInputs] = useState(initialData);

  const handleSearchVal = (e) => {
    const value = e.target.value;
    setSearchInputs({
      ...searchInputs,
      [e.target.name]: value,
    });
  };

  const handleDropdownChange = (e) => {
    const value = e.target.value;
    const next = { ...searchInputs, [e.target.name]: value };
    setSearchInputs(next);
    setGetListParams({
      ...paramsObject,
      ...next,
      pageNo: 0,
    });
  };

  const handleB2CRegisteredDateChange = (dates) => {
    const [start, end] = dates || [null, null];
    const registered_from = start ? moment(start).format("YYYY-MM-DD") : "";
    const registered_to = end ? moment(end).format("YYYY-MM-DD") : "";
    const updatedSearchInputs = {
      ...searchInputs,
      registered_from,
      registered_to,
    };
    setSearchInputs(updatedSearchInputs);
    setGetListParams({
      ...paramsObject,
      ...updatedSearchInputs,
      pageNo: 0,
    });
  };

  const handleB2BRegisteredDateChange = (dates) => {
    const [start, end] = dates || [null, null];
    const business_registered_from = start ? moment(start).format("YYYY-MM-DD") : "";
    const business_registered_to = end ? moment(end).format("YYYY-MM-DD") : "";
    const updatedSearchInputs = {
      ...searchInputs,
      business_registered_from,
      business_registered_to,
    };
    setSearchInputs(updatedSearchInputs);
    setGetListParams({
      ...paramsObject,
      ...updatedSearchInputs,
      pageNo: 0,
    });
  };

  const b2cDateRangeStart = searchInputs.registered_from
    ? new Date(searchInputs.registered_from)
    : null;
  const b2cDateRangeEnd = searchInputs.registered_to
    ? new Date(searchInputs.registered_to)
    : null;
  const b2bDateRangeStart = searchInputs.business_registered_from
    ? new Date(searchInputs.business_registered_from)
    : null;
  const b2bDateRangeEnd = searchInputs.business_registered_to
    ? new Date(searchInputs.business_registered_to)
    : null;

  const exportCustomersMutation = useMutation(
    (params) => exportCustomers(params),
    {
      onSuccess: (res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "customers-export.csv";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Export downloaded");
      },
      onError: (err) => {
        toast.error(err?.message || "Export failed");
      },
    }
  );

  const handleExport = () => {
    exportCustomersMutation.mutate({ ...paramsObject });
  };

  const limit = paramsObject.limit || 50;

  const { setDataToServer: updateStatusMutation } = useCreate({
    url: "customers/update_status",
    refreshUrl: "customers/list",
    onSuccess: () => toast.success("Customer status updated"),
    onError: (err) => toast.error(err?.response?.data?.message || "Failed to update status"),
  });

  const handleBlockUnblock = (customerId, currentStatus) => {
    updateStatusMutation.mutate({
      customer_id: customerId,
      status: currentStatus === "blocked" ? "active" : "blocked",
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between bg-body_bg shadow px-4 py-2">
        <h2 className="font-semibold text-3xl">Customers</h2>
        <PrimaryButton
          label={exportCustomersMutation.isPending ? "Exporting…" : "Export"}
          onClick={handleExport}
          disabled={exportCustomersMutation.isPending}
        />
      </div>
      <div className="px-4 py-3">
        <div className="space-y-3">
          {/* Filter bar */}
          <div className="bg-white rounded-lg shadow p-4 border border-gray-100 w-full">
            <div className="flex flex-wrap gap-3 items-end w-full">
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  Search
                </label>
                <input
                  name="searchQuery"
                  onChange={handleSearchVal}
                  onKeyDown={(e) => handleKeyDown(e, searchInputs)}
                  value={searchInputs.searchQuery}
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded text-sm"
                  placeholder="Search..."
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  Name
                </label>
                <input
                  name="name"
                  onChange={handleSearchVal}
                  onKeyDown={(e) => handleKeyDown(e, searchInputs)}
                  value={searchInputs.name}
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded text-sm"
                  placeholder="Name"
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  Phone
                </label>
                <input
                  name="phone"
                  onChange={handleSearchVal}
                  onKeyDown={(e) => handleKeyDown(e, searchInputs)}
                  value={searchInputs.phone}
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded text-sm"
                  placeholder="Phone"
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  Email
                </label>
                <input
                  name="email"
                  onChange={handleSearchVal}
                  onKeyDown={(e) => handleKeyDown(e, searchInputs)}
                  value={searchInputs.email}
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded text-sm"
                  placeholder="Email"
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  City
                </label>
                <input
                  name="city"
                  onChange={handleSearchVal}
                  onKeyDown={(e) => handleKeyDown(e, searchInputs)}
                  value={searchInputs.city}
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded text-sm"
                  placeholder="City"
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  State
                </label>
                <input
                  name="state"
                  onChange={handleSearchVal}
                  onKeyDown={(e) => handleKeyDown(e, searchInputs)}
                  value={searchInputs.state}
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded text-sm"
                  placeholder="State"
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  Customer Type
                </label>
                <select
                  name="customer_type"
                  onChange={handleDropdownChange}
                  value={searchInputs.customer_type ?? ""}
                  className="w-full p-2 border border-gray-200 rounded text-sm"
                >
                  <option value="">All</option>
                  <option value="b2c">B2C</option>
                  <option value="b2b">B2B</option>
                </select>
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  Status
                </label>
                <select
                  name="status"
                  onChange={handleDropdownChange}
                  value={searchInputs.status ?? "active"}
                  className="w-full p-2 border border-gray-200 rounded text-sm"
                >
                  <option value="active">Active</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  Limit
                </label>
                <select
                  name="limit"
                  value={data?.data?.per_page ?? paramsObject.limit}
                  onChange={handleSetLimit}
                  className="w-full p-2 border border-gray-200 rounded text-sm"
                >
                  {[10, 20, 30, 40, 50].map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            {data.isLoading || data.isFetching ? (
              <Spinner />
            ) : data.status === "error" ? (
              <Errors errorObject={data.error} inline />
            ) : (
              <>
                <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-100">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16 text-center">Sr.no</TableHead>
                        <TableHead className="w-16 text-center">Type</TableHead>
                        <TableHead className="min-w-[140px] text-center">Customer Name</TableHead>
                        <TableHead className="w-28 text-center">Phone</TableHead>
                        <TableHead className="min-w-[160px] text-center">Email</TableHead>
                        <TableHead className="min-w-[180px] text-center">Address</TableHead>
                        <TableHead className="w-24 text-center">City</TableHead>
                        <TableHead className="w-24 text-center">State</TableHead>
                        <TableHead className="w-20 text-center">Pincode</TableHead>
                        <TableHead className="w-24 text-center">Status</TableHead>
                        <TableHead className="w-28 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.data?.data?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} className="text-center py-10 text-gray-500 font-medium">
                            No Data Found
                          </TableCell>
                        </TableRow>
                      ) : (
                        data?.data?.data?.map((customerData, idx) => {
                          const {
                            id,
                            phone,
                            name,
                            email,
                            address,
                            city,
                            state,
                            pincode,
                            is_business,
                            disabled_on,
                            status: backendStatus,
                            registered_at,
                            created_at,
                            business_registered_at,
                            business_created_at,
                          } = customerData;
                          const status = (backendStatus === "blocked" || disabled_on) ? "blocked" : "active";
                          const b2cDate = registered_at ?? created_at;
                          const b2bDate = business_registered_at ?? business_created_at;
                          const formatDate = (val) => {
                            if (!val) return "---";
                            const d = typeof val === "number" ? new Date(val * 1000) : new Date(val);
                            return isNaN(d.getTime()) ? "---" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
                          };
                          return (
                            <TableRow key={id}>
                              <TableCell className="text-center w-16">
                                {paramsObject.limit * paramsObject.pageNo + idx + 1}
                              </TableCell>
                              <TableCell className="text-center w-16">
                                {is_business ? "B2B" : "B2C"}
                              </TableCell>
                              <TableCell className="text-center min-w-[140px]">
                                {name || "---"}
                              </TableCell>
                              <TableCell className="text-center w-28">
                                {phone || "---"}
                              </TableCell>
                              <TableCell className="text-center min-w-[160px]">
                                {email || "---"}
                              </TableCell>
                              <TableCell className="text-center min-w-[180px]">
                                {address || "---"}
                              </TableCell>
                              <TableCell className="text-center w-24">
                                {city || "---"}
                              </TableCell>
                              <TableCell className="text-center w-24">
                                {state || "---"}
                              </TableCell>
                              <TableCell className="text-center w-20">
                                {pincode || "---"}
                              </TableCell>
                              <TableCell className="text-center w-24">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    status === "active"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {status === "active" ? "Active" : "Blocked"}
                                </span>
                              </TableCell>
                              <TableCell className="text-center w-28">
                                <button
                                  type="button"
                                  onClick={() => handleBlockUnblock(id, status)}
                                  disabled={updateStatusMutation.isPending}
                                  className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                                    status === "blocked"
                                      ? "border-green-600 text-green-700 bg-white hover:bg-green-50"
                                      : "border-red-600 text-red-700 bg-white hover:bg-red-50"
                                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                  {status === "blocked" ? "Unblock" : "Block"}
                                </button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="my-4">
                  <Pagination
                    currentPage={paramsObject.pageNo}
                    lengthofItems={data?.data?.total_count}
                    limit={limit}
                    onPageChange={handlePageClick}
                    pageCount={Math.ceil(
                      (data?.data?.total_count || 0) /
                        (data?.data?.per_page || limit)
                    )}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Customers;
